import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  createShopifyCart,
  getShopifyCart,
  addToShopifyCart,
  updateShopifyCart,
  removeShopifyCart,
} from "@/lib/shop";

const CartContext = createContext(null);
const CART_KEY = "wc_shopify_cart_id";

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load existing cart on mount
  useEffect(() => {
    (async () => {
      const id = localStorage.getItem(CART_KEY);
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const c = await getShopifyCart(id);
        if (c) setCart(c);
        else localStorage.removeItem(CART_KEY);
      } catch {
        localStorage.removeItem(CART_KEY);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const ensureCart = useCallback(async () => {
    if (cart?.id) return cart;
    const fresh = await createShopifyCart([]);
    localStorage.setItem(CART_KEY, fresh.id);
    setCart(fresh);
    return fresh;
  }, [cart]);

  const addItem = useCallback(
    async (merchandiseId, quantity = 1) => {
      const current = await ensureCart();
      const updated = await addToShopifyCart(current.id, merchandiseId, quantity);
      setCart(updated);
      setDrawerOpen(true);
      return updated;
    },
    [ensureCart]
  );

  const updateLine = useCallback(
    async (lineId, quantity) => {
      if (!cart?.id) return;
      if (quantity <= 0) {
        const updated = await removeShopifyCart(cart.id, [lineId]);
        setCart(updated);
      } else {
        const updated = await updateShopifyCart(cart.id, lineId, quantity);
        setCart(updated);
      }
    },
    [cart]
  );

  const removeLine = useCallback(
    async (lineId) => {
      if (!cart?.id) return;
      const updated = await removeShopifyCart(cart.id, [lineId]);
      setCart(updated);
    },
    [cart]
  );

  const clearCart = useCallback(() => {
    localStorage.removeItem(CART_KEY);
    setCart(null);
  }, []);

  const goToCheckout = () => {
    if (cart?.checkoutUrl) {
      window.location.href = cart.checkoutUrl;
    }
  };

  const lineEdges = cart?.lines?.edges || [];
  const items = lineEdges.map((e) => {
    const l = e.node;
    const m = l.merchandise;
    const size = m.selectedOptions?.find((o) => o.name.toLowerCase() === "size")?.value || m.title;
    return {
      id: l.id,
      quantity: l.quantity,
      merchandiseId: m.id,
      variantTitle: m.title,
      size,
      product: {
        handle: m.product?.handle,
        name: m.product?.title,
        image: m.image?.url,
        price: parseFloat(m.price?.amount || "0"),
      },
      subtotal: parseFloat(m.price?.amount || "0") * l.quantity,
    };
  });

  const total = cart?.cost?.totalAmount?.amount
    ? parseFloat(cart.cost.totalAmount.amount)
    : items.reduce((s, i) => s + i.subtotal, 0);
  const count = cart?.totalQuantity || 0;
  const currency = cart?.cost?.totalAmount?.currencyCode || "USD";

  return (
    <CartContext.Provider
      value={{
        cart,
        items,
        total,
        count,
        currency,
        loading,
        addItem,
        updateLine,
        removeLine,
        clearCart,
        drawerOpen,
        setDrawerOpen,
        goToCheckout,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
